
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { DateTime } from 'luxon';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { addStylingToHorizontalHeader, downloadFile } from '@/utils/exceljs-helpers';
import { decodeMenu } from '@/utils/decoding';
import { CATERING_OPEN_ITEM } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import omitBy from 'lodash/omitBy';
import * as ExcelJS from 'exceljs';
import Toast from '@/utils/toast';
import ListItem from '@/components/shared/ListItem.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import PageLoader from '@/components/shared/PageLoader.vue';
import MenuEditor from '@/components/menu/MenuEditor.vue';

const namespace: string = 'menus';

@Component<MenuList>({
	components: {
		ListItem,
		ModalWrapper,
		StandardModal,
		PageLoader,
		MenuEditor
	}
})
export default class MenuList extends Vue {
	@Action('fetchMenus', { namespace }) private fetchMenus!: () => Promise<Menu[]>;
	@Action('deleteMenu', { namespace }) private deleteMenu!: (menu: Menu) => Promise<void>;
	@Action('updateMenu', { namespace }) private updateMenu!: (payload: object) => Promise<void>;
	@Action('duplicateMenu', { namespace }) private duplicateMenu!: (menu: Menu) => Promise<void>;
	@Action('deepGetMenu', { namespace }) private deepGetMenu!: (menuId: number) => Promise<Menu>;
	@Action('setSelectedMenu', { namespace }) private setSelectedMenu!: (menu: Menu | object) => Promise<void>;
	@Action('fetchEvents', { namespace: 'events' }) private fetchEvents!: () => Promise<void>;
	@Action('setSelectedEvent', { namespace: 'events' }) private setSelectedEvent!: (event: SuitesEvent|null) => void;
	@Getter('getSelectedMenu', { namespace }) private selectedMenu!: Menu;
	@Getter('getMenus', { namespace }) private menusData!: Menu[];
	@Getter('getFetchMenusComplete', { namespace }) private fetchMenusComplete!: boolean;
	@Getter('areSuitesEnabled', { namespace: 'auth' }) private areSuitesEnabled!: boolean;
	@Getter('isPosIntegrated', { namespace: 'auth' }) private isPosIntegrated!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Prop({ type: Object, default: () => {} }) private restaurant!: Restaurant;

	private PERMISSION = PERMISSION;
	private showDialog: boolean = false;
	private isCreating: boolean = false;
	private showDeleteModal: boolean = false;
	private loading: boolean = false;
	private showConfirmationModal: string|null = '';
	private selectedLocale: RestaurantLocale|null = null;

	private itemToDelete: Menu = {} as Menu;
	private selectedMenuBeforeEditSnapshot: Menu = {} as Menu;
	private menuToCopy: Menu = {} as Menu;

	private headerRowStyles: Partial<ExcelJS.Style> = {
		font: {
			size: 11,
			bold: true
		},
		fill: {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFB7B7B7' }
		},
		border: {
			top: { style: 'thin', color: { argb: '000000' } },
			right: { style: 'thin', color: { argb: '000000' } },
			bottom: { style: 'thin', color: { argb: '000000' } },
			left: { style: 'thin', color: { argb: '000000' } }
		}
	};

	private actionsArray: ActionItem<Menu>[] = [
		{
			iconName: 'mdi-archive-arrow-down',
			buttonLabel: this.$t('shared.actions_array.btn_archive'),
			onClick: async (evt: object, menu: Menu): Promise<void> => {
				await this.updateMenu({ menu: { ...menu, published: false } })
					.catch((errorMessage: string) => {
						Toast.error(this.$t('shared.error_archiving', { name: menu.name, errorMessage }));
					});
			},
			permission: PERMISSION.MENU_ARCHIVE
		},
		{
			iconName: 'mdi-archive-arrow-up',
			buttonLabel: this.$t('shared.actions_array.btn_publish'),
			onClick: async (evt: object, menu: Menu): Promise<void> => {
				this.publishAttempt(menu);
			},
			permission: PERMISSION.MENU_ARCHIVE,
			disabled: (menu: Menu) => menu.name === CATERING_OPEN_ITEM.MENU_NAME
		},
		{
			iconName: 'mdi-pencil',
			buttonLabel: this.$t('shared.actions_array.btn_edit'),
			onClick: async (evt: object, menu: Menu): Promise<void> => {
				await this.setSelectedMenu(menu)
					.then(() => this.openEditor(false))
					.catch((errorMessage: string) => {
						Toast.error(this.$t('shared.error_updating', { name: menu.name, errorMessage }));
					});
			},
			permission: PERMISSION.MENU_EDIT
		},
		{
			iconName: 'mdi-file-multiple',
			buttonLabel: this.$t('shared.actions_array.btn_duplicate'),
			onClick: async (evt: object, menu: Menu): Promise<void> => {
				this.$emit('duplicating', true);
				await this.duplicateMenu(menu)
					.catch((errorMessage: string) => {
						Toast.error(this.$t('shared.error_duplicating', { name: menu.name, errorMessage }));
					})
					.finally(() => {
						this.$emit('duplicating', false);
					});
			},
			permission: PERMISSION.MENU_DUPLICATE
		},
		{
			iconName: 'mdi-file-export',
			buttonLabel: this.$t('shared.actions_array.btn_export'),
			onClick: async (evt: object, menu: Menu): Promise<void> => {
				await this.exportMenuToXlsx(menu)
					.catch((errorMessage: string) => {
						Toast.error(this.$t('shared.error_exporting', { name: menu.name, errorMessage }));
					});
			},
			permission: PERMISSION.MENU_EXPORT
		},
		{
			iconName: 'mdi-delete',
			buttonLabel: this.$t('shared.actions_array.btn_delete'),
			onClick: (evt: object, menu: Menu): void => {
				this.setItemToDeleteName(menu);
			},
			permission: PERMISSION.MENU_DELETE
		},
		{
			iconName: 'mdi-window-close',
			buttonLabel: this.$t('shared.actions_array.btn_cancel'),
			onClick: null
		}
	];

	private get secondaryLocales(): RestaurantLocale[] {
		return this.restaurant?.locales?.filter((locale: RestaurantLocale) => !locale.is_default) || [];
	}

	private get menus(): Menu[] {
		return (this.menusData || []).map(
			(menu: Menu): Menu => {
				return { to: { name: menu.allow_sections ? 'Sections' : 'MenuItems', params: { menu_id: menu.id } }, ...menu };
			}
		);
	}

	private get publishedMenus(): Menu[] {
		return this.menus.filter(menu => menu.published_at);
	}

	private get unpublishedMenus(): Menu[] {
		return this.menus.filter(menu => !menu.published_at);
	}

	/**
	 * Attempt to publish the menu. We throw a warning if the menu has children
	 * to warn the user that all of its content will be published.
	 *
	 * @param {Menu} menu
	 * @return {void}
	 */
	private publishAttempt(menu: Menu): void {
		this.menuToCopy = menu;
		if((menu.sections && menu.sections.length) || (menu.items && menu.items.length)) {
			this.showConfirmationModal = 'confirmPublish';
		}
		else {
			this.publish();
		}
	}

	/**
	 * Publish the menu
	 *
	 * @return {void}
	 */
	private async publish(): Promise<void> {
		if(this.menuToCopy) {
			await this.updateMenu({ menu: { ...this.menuToCopy, published: true } })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_publishing', { name: this.menuToCopy.name, errorMessage }));
				});
			this.showConfirmationModal = null;
		}
	}

	/**
	 * Open Menu Modal
	 * Called by EditMenus.vue to open the menu editor
	 *
	 * @param {boolean} creating
	 * @return {void}
	 */
	public openEditor(creating: boolean): void {
		this.showDialog = true;
		this.isCreating = creating;

		document.documentElement.classList.add('modal-open');
	}

	/**
	 * Capture the change-detection snapshot once the menu editor has finished
	 * initializing (defaults applied during editor init, ie. catering/K12 defaults, etc.)
	 *
	 * @return {void}
	 */
	private captureSnapshot(): void {
		this.selectedMenuBeforeEditSnapshot = cloneDeep(this.selectedMenu);
	}

	/**
	 * Set the item name for the deletion modal
	 *
	 * @return {void}
	 * @param menu
	 */
	private setItemToDeleteName(menu: Menu): void {
		this.showDeleteModal = true;
		this.itemToDelete = menu;
	}

	/**
	 * Cancel closing of modal through the confirmation close modal
	 *
	 * @return {void}
	 */
	private cancelCloseModal(): void {
		this.showConfirmationModal = null;
	}

	/**
	 * Check if needs to show the confirm close modal to the user
	 * We need to get rid of the undefined properties first.
	 *
	 * @return {void}
	 */
	private confirmClose(saved: boolean = false) {
		const selectedMenuToCompare = omitBy(this.selectedMenu, isNil);
		const selectedMenuBeforeEditSnapshotToCompare = omitBy(this.selectedMenuBeforeEditSnapshot, isNil);
		this.selectedLocale = null;
		if (saved || isEqual(selectedMenuToCompare, selectedMenuBeforeEditSnapshotToCompare)) {
			this.close(saved);
		}
		else {
			this.showConfirmationModal = 'confirmClose';
		}
	}

	/**
	 * Close the Menu Editor modal
	 *
	 * @return {void}
	 */
	private async close(saved: boolean): Promise<void> {
		this.showConfirmationModal = null;
		this.showDialog = false;
		this.isCreating = false;

		// Remove the selected menu from the store
		this.setSelectedMenu({});
		this.selectedMenuBeforeEditSnapshot = {} as Menu;
		document.documentElement.classList.remove('modal-open');

		// Re-fetch the menus if the modal was simply closed
		if (!saved) {
			await this.fetchMenus()
				.catch((errorMessage) => {
					Toast.error(this.$t('menu.error_fetching', { errorMessage }));
				});
		}
	}

	private cancel(): void {
		this.showDeleteModal = false;
		this.itemToDelete = {} as Menu;
	}

	/**
	 * User confirmed deletion of the item, so we delete it and close the modal.
	 * If the user is able to access suites/events, we refetch them in case a menu assigned to an event suite was deleted.
	 *
	 * @return {Promise<void>}
	 */
	private async confirm(): Promise<void> {
		this.loading = true;
		await this.deleteMenu(this.itemToDelete)
			.then(async () => {
				if (this.areSuitesEnabled && this.hasPermission(PERMISSION.ROUTE_EVENTS)) {
					await this.fetchEvents();
					this.setSelectedEvent(null);
				}
			})
			.catch((errorMessage) => {
				Toast.error(this.$t('shared.error_deleting', { name: this.itemToDelete.name, errorMessage }));
			});
		this.itemToDelete = {} as Menu;
		this.loading = false;
		this.showDeleteModal = false;
	}

	/**
	 * Generate locale-specific columns for each secondary locale
	 * For example for "Menu Name" it would output "Menu Name FR" header and "menu_name_fr" key if french is secondary language
	 *
	 * @param {string} baseKey - The base column key (e.g., 'menu_name')
	 * @param {string} i18nKey - The i18n key for the header label
	 * @param {number} width
	 * @return {Partial<ExcelJS.Column>[]}
	 */
	private getLocaleColumns(baseKey: string, i18nKey: string, width: number): Partial<ExcelJS.Column>[] {
		return this.secondaryLocales.map((locale: RestaurantLocale) => ({
			header: `${this.$t(i18nKey)} ${locale.locale_short.toUpperCase()}`,
			key: `${baseKey}_${locale.locale_short}`,
			width
		}));
	}

	/**
	 * Export the menu to a XSLX file
	 *
	 * @param {Menu} menu
	 * @return {Promise<void>}
	 */
	private async exportMenuToXlsx(menu: Menu): Promise<void> {
		const workbook: ExcelJS.Workbook = new ExcelJS.Workbook();
		// If full menu is null, then options will be empty
		const rawMenu: Menu | null = menu.id ? await this.deepGetMenu(menu.id) : null;
		const fullMenu: Menu = decodeMenu(rawMenu || menu, true);
		const date = DateTime.now().toISODate();

		// Create worksheets for items and options
		this.createItemsPage(workbook, fullMenu);
		this.createOptionsPage(workbook, fullMenu);

		// Save the workbook to a file
		workbook.xlsx.writeBuffer().then(buffer => {
			downloadFile(buffer, this.$t('menu.menu_export.file_name', { menuName: menu.name, date }));
		});
	}

	/**
	 * Create the item list page in the Excel workbook
	 *
	 * @param {ExcelJS.Workbook} workbook
	 * @param {Menu} menu
	 * @return {Promise<void>}
	 */
	private async createItemsPage(workbook: ExcelJS.Workbook, menu: Menu): Promise<void> {
		const worksheet: ExcelJS.Worksheet = workbook.addWorksheet(this.$t('menu.menu_export.item_sheet_name'), {
			pageSetup: { orientation: 'landscape' }
		});
		worksheet.columns = [
			{ header: this.$t('menu.menu_export.item_sheet_columns.jde_category'), key: 'jde_category', width: 20 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.menu_name'), key: 'menu_name', width: 20 },
			...this.getLocaleColumns('menu_name', 'menu.menu_export.item_sheet_columns.menu_name', 20),
			{ header: this.$t('menu.menu_export.item_sheet_columns.section_name'), key: 'section_name', width: 20 },
			...this.getLocaleColumns('section_name', 'menu.menu_export.item_sheet_columns.section_name', 20),
			{ header: this.$t('menu.menu_export.item_sheet_columns.recipe_number'), key: 'recipe_number', width: 20 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.meal_package'), key: 'meal_package', width: 15 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.short_name'), key: 'short_name', width: 25 },
			...this.getLocaleColumns('short_name', 'menu.menu_export.item_sheet_columns.short_name', 25),
			{ header: this.$t('menu.menu_export.item_sheet_columns.description'), key: 'description', width: 40 },
			...this.getLocaleColumns('description', 'menu.menu_export.item_sheet_columns.description', 40),
			{ header: this.$t('menu.menu_export.item_sheet_columns.base_price'), key: 'base_price', width: 20 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.sell_price'), key: 'sell_price', width: 10 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.min_quantity'), key: 'min_quantity', width: 18 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.allergens'), key: 'allergens', width: 25 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.vegan_tag'), key: 'vegan_tag', width: 10 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.vegetarian_tag'), key: 'vegetarian_tag', width: 15 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.kcal'), key: 'kcal', width: 10 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.option_group'), key: 'option_group', width: 40 },
			{ header: this.$t('menu.menu_export.item_sheet_columns.options'), key: 'options', width: 40 }
		];
		let itemData: { [key: string]: string | number | null }[] = [];
		if (menu.sections?.length) {
			itemData = menu.sections.map((section: MenuSection) => {
				return section!.items?.map((item: MenuItem) => this.formatItemData(item, menu, section)) || [];
			}).flat();
		}
		else {
			itemData = menu.items?.map((item: MenuItem) => this.formatItemData(item, menu)) || [];
		}
		worksheet.addRows(itemData);
		addStylingToHorizontalHeader(worksheet, 1, this.headerRowStyles);
	}

	/**
	 * Format the item data for the Excel sheet
	 *
	 * @param {MenuItem} item
	 * @param {Menu} menu
	 * @param {MenuSection} [section]
	 * @return {{ [key: string]: string | number }}
	 */
	private formatItemData(item: MenuItem, menu: Menu, section?: MenuSection): { [key: string]: string | number } {
		const data: { [key: string]: string | number } = {
			jde_category: item.category || '',
			menu_name: menu.name,
			section_name: section?.name || '',
			recipe_number: item.recipe_number || '',
			meal_package: item.meal_package ? this.$t('menu.menu_export.item_sheet_columns.yes') : this.$t('menu.menu_export.item_sheet_columns.no'),
			short_name: item.name,
			description: item.description || '',
			base_price: item.base_price ? this.$n(+item.base_price, 'currency') : '',
			sell_price: this.$n(+item.price, 'currency'),
			min_quantity: item.min_quantity && item.min_quantity > 1 ? item.min_quantity : '',
			allergens: item.allergens || '',
			vegan_tag: item.diets?.includes('Vegan') ? this.$t('menu.menu_export.item_sheet_columns.vegan') : '',
			vegetarian_tag: item.diets?.includes('Vegetarian') ? this.$t('menu.menu_export.item_sheet_columns.vegetarian') : '',
			kcal: item.calories || '',
			option_group: item.options?.map((option: OptionGroup) => option.name).join(', ') || '',
			options: item.options?.map((option: OptionGroup) => option.values?.map((opt: Option) => opt.name).join(', ')).join(', ') || ''
		};

		// Add columns for each secondary locale setup in the location
		this.secondaryLocales.forEach((locale: RestaurantLocale) => {
			const loc = locale.locale_short;
			data[`menu_name_${loc}`] = menu.localization?.[loc]?.name || '';
			data[`section_name_${loc}`] = section?.localization?.[loc]?.name || '';
			data[`short_name_${loc}`] = item.localization?.[loc]?.name || '';
			data[`description_${loc}`] = item.localization?.[loc]?.description || '';
		});

		return data;
	}

	/**
	 * Create the option list page in the Excel workbook
	 *
	 * @param {ExcelJS.Workbook} workbook
	 * @param {Menu} menu
	 * @return {void}
	 */
	private createOptionsPage(workbook: ExcelJS.Workbook, menu: Menu): void {
		const worksheet: ExcelJS.Worksheet = workbook.addWorksheet(this.$t('menu.menu_export.option_sheet_name'), {
			pageSetup: { orientation: 'landscape' }
		});
		worksheet.columns = [
			{ header: this.$t('menu.menu_export.option_sheet_columns.option_group_name'), key: 'option_group_name', width: 30 },
			...this.getLocaleColumns('option_group_name', 'menu.menu_export.option_sheet_columns.option_group_name', 30),
			{ header: this.$t('menu.menu_export.option_sheet_columns.description'), key: 'description', width: 30 },
			...this.getLocaleColumns('description', 'menu.menu_export.option_sheet_columns.description', 30),
			{ header: this.$t('menu.menu_export.option_sheet_columns.list_of_items'), key: 'list_of_items', width: 40 },
			{ header: this.$t('menu.menu_export.option_sheet_columns.option_name'), key: 'option_name', width: 30 },
			...this.getLocaleColumns('option_name', 'menu.menu_export.option_sheet_columns.option_name', 30),
			{ header: this.$t('menu.menu_export.option_sheet_columns.sell_price'), key: 'sell_price', width: 10 },
			{ header: this.$t('menu.menu_export.option_sheet_columns.kcal'), key: 'kcal', width: 10 }
		];

		const optionData: { [key: string]: string | number }[] = [];
		if (menu.sections?.length) {
			menu.sections.forEach((section: MenuSection) => {
				section.items?.forEach((item: MenuItem) => {
					if (item.options?.length) {
						this.formatOptionData(item.options, item, optionData);
					}
				});
			});
		}
		else {
			menu.items?.forEach((item: MenuItem) => {
				if (item.options?.length) {
					this.formatOptionData(item.options, item, optionData);
				}
			});
		}
		worksheet.addRows(optionData);
		addStylingToHorizontalHeader(worksheet, 1, this.headerRowStyles);
	}

	/**
	 * Format the option data for the Excel sheet
	 *
	 * @param {OptionGroup[]} optionGroups
	 * @param {MenuItem} item
	 * @param {object[]} optionData
	 * @return {void}
	 */
	private formatOptionData(optionGroups: OptionGroup[], item: MenuItem, optionData: { [key: string]: string | number }[]): void {
		optionGroups.forEach((optionGroup: OptionGroup) => {
			optionGroup.values?.forEach((option: Option) => {
				// Check if the option already exists in the optionData array and append the item name if it does
				const optionIndex = optionData.findIndex((opt: { [key: string]: string | number }) => opt.id === option.id);
				if (optionIndex !== -1) {
					optionData[optionIndex].list_of_items += `, ${item.name}`;
				}
				else {
					const optionEntry: { [key: string]: string | number } = {
						id: option.id || '',
						option_group_name: optionGroup.name,
						description: optionGroup.description || '',
						list_of_items: item.name,
						option_name: option.name,
						sell_price: this.$n(+(option.price || 0), 'currency'),
						kcal: option.calories || ''
					};

					// Add columns for each secondary locale setup in the location
					this.secondaryLocales.forEach((locale: RestaurantLocale) => {
						const loc = locale.locale_short;
						optionEntry[`option_group_name_${loc}`] = optionGroup.localization?.[loc]?.name || '';
						optionEntry[`description_${loc}`] = optionGroup.localization?.[loc]?.description || '';
						optionEntry[`option_name_${loc}`] = option.localization?.[loc]?.name || '';
					});

					optionData.push(optionEntry);
				}
				// If the option has sub-options, format them as well
				if (option.options) {
					this.formatOptionData(option.options, item, optionData);
				}
			});
		});
	}
}
